import {
  BrandDesignSystemProvider,
  BrandFontLoader,
  BrandGuidelinesProvider,
  getTemplate,
} from '@magig/creative/react';
import {
  type CreativeAssetFormat,
  creativeAssetFormatEnum,
  DEFAULT_BRAND_DESIGN_SYSTEM,
  DEFAULT_BRAND_GUIDELINES,
} from '@magig/schemas';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

// [i18n wave 7] URL-driven template probe. The measurement surface for
// scripts/i18n-creative-overflow.mjs.
//
// WHY A SECOND DEV ROUTE, next to dev.preview:
//   dev.preview is for a human — it scales the canvas down to fit a
//   720px-tall viewport (MAX_PREVIEW_HEIGHT) and holds template/format/
//   fill in useState. Both are wrong for a measuring rig: a scaled
//   canvas makes every box measurement a ratio you have to trust, and
//   useState can't be driven from a URL. This route takes its inputs
//   from search params and renders at the format's EXACT dimensions,
//   pinned to (0, 0) — the same envelope render.creative.$id uses, so
//   what the probe measures is what the render worker screenshots.
//
// WHY NOT THE REAL ROUTE: /render/creative/$id needs a creative_assets
// row, which needs a seeded database. This route needs neither — the
// fill arrives in the URL and the brand falls through to the neutral
// defaults, so the rig runs against a bare `pnpm dev` dashboard.
//
// Gated behind import.meta.env.DEV, and excluded from the i18n scanner
// by the /routes/dev. non-goal pattern — bare English below is correct.

const searchSchema = z.object({
  /** Template key, e.g. 'hook-card-v1'. */
  template: z.string().min(1),
  /** Format to render at. Decides the canvas dimensions. */
  format: creativeAssetFormatEnum,
  /** The fill. Accepted as EITHER a JSON string or an already-parsed
   *  object, because TanStack Router's default search parser runs
   *  JSON.parse over every param before validateSearch sees it — a
   *  `fill={"headline":…}` in the URL arrives here as an object, and a
   *  plain `z.string()` rejects it with "expected string, received
   *  object". Taking both keeps the URL hand-pasteable (you can read
   *  the fill in the address bar, which is the point of a dev route)
   *  without depending on that parsing behaviour staying put.
   *
   *  Validated against the template's own schema below — strictly, like
   *  the render route, so a malformed probe fill reports as an error
   *  state rather than a broken layout the script would then blame on
   *  the template. */
  fill: z.union([z.string().min(1), z.record(z.string(), z.unknown())]),
});

export const Route = createFileRoute('/dev/template-probe')({
  validateSearch: (s) => searchSchema.parse(s),
  component: TemplateProbe,
});

/** Machine-readable outcome, read by the script off
 *  `[data-probe-state]`. Anything other than 'ready' means there is
 *  nothing to measure and the reason is in `[data-probe-error]`. */
type ProbeState = 'ready' | 'error';

function TemplateProbe() {
  if (!import.meta.env.DEV) {
    return <ProbeError message="dev-only route. only loaded in development builds." />;
  }
  return <Probe />;
}

function Probe() {
  const { template: templateKey, format, fill: fillJson } = Route.useSearch();

  const template = getTemplate(templateKey);
  if (template === null) {
    return <ProbeError message={`no static template registered for key "${templateKey}"`} />;
  }

  if (!template.formats.includes(format)) {
    return (
      <ProbeError
        message={`template "${templateKey}" does not support format "${format}" (supports: ${template.formats.join(', ')})`}
      />
    );
  }

  let raw: unknown;
  if (typeof fillJson === 'string') {
    try {
      raw = JSON.parse(fillJson);
    } catch (e) {
      return (
        <ProbeError message={`fill is not valid JSON: ${e instanceof Error ? e.message : e}`} />
      );
    }
  } else {
    raw = fillJson;
  }

  const parsed = template.schema.safeParse(raw);
  if (!parsed.success) {
    return (
      <ProbeError
        message={`fill schema mismatch: ${parsed.error.issues.map((i) => i.message).join('; ')}`}
      />
    );
  }

  const TemplateComponent = template.Component;
  return (
    <ProbeShell state="ready">
      <BrandFontLoader guidelines={DEFAULT_BRAND_GUIDELINES} />
      <BrandGuidelinesProvider guidelines={DEFAULT_BRAND_GUIDELINES}>
        <BrandDesignSystemProvider designSystem={DEFAULT_BRAND_DESIGN_SYSTEM}>
          <TemplateComponent fill={parsed.data} format={format as CreativeAssetFormat} />
        </BrandDesignSystemProvider>
      </BrandGuidelinesProvider>
    </ProbeShell>
  );
}

/** Same fixed overlay as render.creative.$id's RenderShell, with two
 *  additions the rig needs: a state attribute to wait on, and
 *  `data-probe-canvas` on the element whose box IS the canvas, so the
 *  script has an unambiguous frame to measure children against.
 *
 *  `overflow: visible` on the canvas node is deliberate and is the
 *  whole point: the real shell clips at the canvas edge, which is
 *  exactly how an overflowing headline becomes an invisible bug. Left
 *  visible, the overflow is measurable. */
function ProbeShell({ state, children }: { state: ProbeState; children: React.ReactNode }) {
  return (
    <div
      data-probe-state={state}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#ffffff',
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      <div
        data-probe-canvas=""
        style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }}
      >
        {children}
      </div>
    </div>
  );
}

function ProbeError({ message }: { message: string }) {
  return (
    <div
      data-probe-state={'error' satisfies ProbeState}
      data-probe-error={message}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#ffffff',
        zIndex: 100,
        padding: 24,
        fontFamily: 'ui-monospace, monospace',
        fontSize: 13,
        color: '#c0392b',
        whiteSpace: 'pre-wrap',
      }}
    >
      {message}
    </div>
  );
}
